/* eslint-disable require-jsdoc */
import { EMPTY, fromEvent, merge, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import type { framework as ChromecastCafReceiverFramework } from 'chromecast-caf-receiver';
// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import type { CastReceiverContext } from 'chromecast-caf-receiver/cast.framework';
// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import type {
  BitrateChangedEvent as CastBitrateChangedEvent,
  ErrorEvent as CastErrorEvent,
  Event as CastEvent,
  MediaStatusEvent as CastMediaStatusEvent,
} from 'chromecast-caf-receiver/cast.framework.events';
import { Adapter, CAFPlayerOptions, Media } from '@tv2-player/unifiedplayer';
import * as mapEvents from '@tv2-player/unifiedplayer/src/core/mapEvents';

import { stringToObject } from '@tv2-player/unifiedplayer/src/util/stringToObject';
import { decode } from '@tv2-player/unifiedplayer/src/util/utf8';
import CORE_EVENTS, {
  ABR_ADAPTATION,
  AD_BEGIN,
  AD_BREAKBEGIN,
  AD_BREAKEND,
  AD_END,
  AD_TIMEUPDATE,
  MEDIA_CANPLAY,
  MEDIA_CANPLAYTHROUGH,
  MEDIA_DURATIONCHANGE,
  YOSPACE_EMSG,
  MEDIA_ENDED,
  MEDIA_ERROR,
  MEDIA_FINISHED,
  MEDIA_LOADEDDATA,
  MEDIA_LOADEDMETADATA,
  MEDIA_LOADSTART,
  MEDIA_PAUSE,
  MEDIA_PLAY,
  MEDIA_PLAYING,
  MEDIA_PROGRESS,
  MEDIA_RATECHANGE,
  MEDIA_SEEKED,
  MEDIA_SEEKING,
  MEDIA_TIMEUPDATE,
  MEDIA_WAITING,
  PLAYER_LOADING,
  PLAYER_SHUTDOWN,
  SCTE_TIMEDEVENTS,
  VIEW_SOURCECHANGE,
} from '@tv2-player/unifiedplayer/src/core/events';
import { NpawPluginManager } from '@tv2-player/unifiedplayer/src/util/NpawPluginManager';
import type { BreakManager, CastPlayer } from './types';

/**
 * A preliminary check if this error actually is fatal or not.
 * @param {object} DetailedErrorCode DetailedErrorCode static object from CAST SDK.
 * @param {*} detailedErrorCode Error codes from CAF.
 */
const fatalError = (DetailedErrorCode: any, detailedErrorCode: any) => {
  return detailedErrorCode !== DetailedErrorCode.MEDIA_ERROR_MESSAGE;
};

/**
 * Normalize errors.
 * @param {*} cast Cast namespace.
 * @param {*} detailedErrorCode Error codes from CAF.
 */
const normalizeErrors = (cast: any, detailedErrorCode: number | undefined) => {
  const { DetailedErrorCode } = cast.framework.events;
  return {
    code: detailedErrorCode,
    message: Object.keys(DetailedErrorCode).find(
      key => DetailedErrorCode[key] === detailedErrorCode
    ),
    isFatal: fatalError(DetailedErrorCode, detailedErrorCode),
  };
};

type Cast = { framework: typeof ChromecastCafReceiverFramework };

/**
 * @extends Adapter
 */
export class CastAdapter implements Adapter {
  private _readyState = 0;
  private _paused = true;
  private _isLive = false;
  private _adsPlaying = false;
  private supportedTypes = ['application/ism+xml', 'application/dash+xml', 'application/x-mpegURL'];

  private _cast: Cast;
  private _player: CastPlayer;
  private _breakManager: BreakManager;
  private _isSeeking: boolean;
  private _isBuffering: boolean;
  private _content$: Subject<any>;
  private _context: CastReceiverContext;
  public npawPluginManager: NpawPluginManager;

  #destroyed: boolean = false;
  constructor(cast: Cast, options: CAFPlayerOptions) {
    this._context = cast.framework.CastReceiverContext.getInstance();
    this._player = this._context.getPlayerManager();
    this._breakManager = this._player.getBreakManager();

    this._cast = cast;
    this._isSeeking = false;
    this._isBuffering = false;

    // Emit media content when updateMedia is called
    this._content$ = new Subject();

    this.npawPluginManager = new NpawPluginManager(this._player, options);
  }

  setVolume(percentage: number): void {
    throw new Error('Method not implemented.');
  }

  get volume(): number {
    throw new Error('Method not implemented.');
  }

  async play() {
    this._player.play();
  }

  pause() {
    this._player.pause();
  }

  stop() {
    this._player.stop();
  }

  get currentTime() {
    const currentTime = this._player.getCurrentTimeSec();
    const absoluteTimeForMediaTime = this._player.getAbsoluteTimeForMediaTime(currentTime);

    if (this._isLive && absoluteTimeForMediaTime) {
      return absoluteTimeForMediaTime;
    }
    return currentTime;
  }

  set currentTime(time) {
    this._player.seek(time);
  }

  get playbackRate() {
    return this._player.getPlaybackRate();
  }

  set playbackRate(rate) {
    throw new Error('Setter "playbackRate" is not supported by CAF');
  } // eslint-disable-line class-methods-use-this

  get seeking() {
    return this._isSeeking;
  }

  get seekable(): TimeRanges {
    throw new Error('Getter "seekable" not implemented by CastAdapter');
  }

  get fullscreen(): boolean {
    throw new Error('Getter "fullscreen" not implemented by CastAdapter');
  }

  get duration() {
    return this._isLive ? Infinity : this._player.getDurationSec();
  }

  get droppedFrames() {
    return this._player.getStats()?.droppedFrames ?? 0;
  }

  get paused() {
    return this.ended || this._paused;
  }

  get bufferLength() {
    return this._player.getStats().bufferingTime ?? 0;
  }

  get buffering() {
    return this._isBuffering;
  }

  // if duration is NaN - its equivalent to ended
  get ended() {
    return Number.isNaN(this.duration);
  }

  get buffered(): TimeRanges {
    throw new Error('Getter "buffered" not implemented by CastAdapter');
  }

  get currentBitrate() {
    return this._player.getStats().streamBandwidth ?? 0;
  }

  get frameRate(): number {
    throw new Error('Getter "frameRate" not implemented by CastAdapter');
  }

  get readyState() {
    return this._readyState;
  }

  get subtitlesAvailable() {
    return this.textTracks() !== null || this.textTracks() !== undefined;
  }

  get subtitlesEnabled() {
    const textTrackManager = this._player.getTextTracksManager();
    const textTracks = this.textTracks();

    if (textTracks === null) return false;

    try {
      return textTrackManager.getActiveIds().includes(textTracks[0].trackId);
    } catch (e) {
      return false;
    }
  }

  enableSubtitles() {
    throw new Error('Function "enableSubtitles" not implemented by CastAdapter');
  }

  disableSubtitles() {
    throw new Error('Function "disableSubtitles" not implemented by CastAdapter');
  }

  skipForward(secToJump: number) {
    throw new Error('Function "skipForward" not implemented by CastAdapter');
  }

  skipBackwards(secToJump: number) {
    throw new Error('Function "skipBackwards" not implemented by CastAdapter');
  }

  requestFullscreen(): ReturnType<HTMLMediaElement['requestFullscreen']> {
    throw new Error('Function "requestFullscreen" not implemented by CastAdapter');
  }

  exitFullscreen() {
    throw new Error('Function "exitFullscreen" not implemented by CastAdapter');
  }

  support(type: string) {
    return this.supportedTypes.includes(type);
  }

  audioTracks() {
    const audioTrackManager = this._player.getAudioTracksManager();
    try {
      return audioTrackManager.getTracks();
    } catch (e) {
      return null;
    }
  }

  textTracks() {
    const textTrackManager = this._player.getTextTracksManager();
    try {
      return textTrackManager.getTracks();
    } catch (e) {
      return null;
    }
  }

  videoTracks() {
    try {
      const tracks = this._player.getMediaInformation()?.tracks;
      const videoTracks = tracks?.filter(track => track.type === 'VIDEO');

      return videoTracks ?? null;
    } catch (error) {
      return null;
    }
  }

  get muted(): boolean {
    throw new Error('Getter "muted" not implemented by CastAdapter');
  }

  set muted(shouldMute) {
    throw new Error('Setter "muted" not implemented by CastAdapter');
  }

  updateMetadata(metadata: any) {
    this._content$.next({ metadata });
  }

  async updateMedia(content: Media) {
    if (content.src !== undefined && content.src !== null) {
      const {
        metadata: { isLive },
      } = content;
      this._readyState = 0;
      this._paused = true;
      this._adsPlaying = false;
      this._isLive = isLive;
    }
    this._content$.next(content);
    this.npawPluginManager.setVideoOptions(content);
  }

  get mediaElement(): HTMLMediaElement {
    throw new Error('Getter "media" not implemented by CastAdapter');
  }

  get container(): HTMLElement {
    throw new Error('Getter "container" not implemented by CastAdapter');
  }

  async destroy() {
    this._content$.complete();
    this.#destroyed = true;
    this.npawPluginManager.destroy();
  }

  get destroyed() {
    return this.#destroyed;
  }

  delegate(
    event: (typeof CORE_EVENTS)[typeof namespace][number],
    namespace: keyof typeof CORE_EVENTS
  ) {
    const { EventType } = this._cast.framework.events;

    const fromPlayerEvent = (
      player: CastPlayer | CastReceiverContext,
      eventName: (typeof EventType)[keyof typeof EventType]
    ): Observable<CastEvent> => {
      return fromEvent(player as any, eventName);
    };

    switch (`${namespace}.${event}`) {
      case YOSPACE_EMSG:
        return fromPlayerEvent(this._player, EventType.EMSG).pipe(
          filter(() => !this._adsPlaying),
          map((emsg: ChromecastCafReceiverFramework.events.EmsgEvent) => ({
            ...emsg,
            messageData: stringToObject(decode(emsg.messageData!)),
          }))
        );
      case MEDIA_CANPLAY:
        return fromPlayerEvent(this._player, EventType.CAN_PLAY).pipe(
          tap(() => {
            this._readyState = 3;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_CANPLAYTHROUGH:
        return fromPlayerEvent(this._player, EventType.CAN_PLAY_THROUGH).pipe(
          tap(() => {
            this._readyState = 4;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_DURATIONCHANGE:
        // The `getDurationSec()` method returns undefined until the `EventType.LOADED_DATA`
        // is emitted, which is why that event is used instead of `EventType.DURATION_CHANGE`
        return fromPlayerEvent(this._player, EventType.LOADED_DATA).pipe(
          filter(() => !this._adsPlaying)
        );
      case MEDIA_FINISHED:
        return fromPlayerEvent(this._player, EventType.MEDIA_FINISHED).pipe(
          filter(() => !this._adsPlaying)
        );
      case MEDIA_ENDED:
        return fromPlayerEvent(this._player, EventType.ENDED).pipe(filter(() => !this._adsPlaying));
      case MEDIA_ERROR:
        return fromPlayerEvent(this._player, EventType.ERROR).pipe(
          map((err: CastErrorEvent) => normalizeErrors(this._cast, err.detailedErrorCode)),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_LOADEDDATA:
        return fromPlayerEvent(this._player, EventType.LOADED_DATA).pipe(
          tap(() => {
            this._readyState = 2;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_LOADEDMETADATA:
        return fromPlayerEvent(this._player, EventType.LOADED_METADATA).pipe(
          tap(() => {
            this._readyState = 1;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_LOADSTART:
        return fromPlayerEvent(this._player, EventType.LOAD_START).pipe(
          filter(() => !this._adsPlaying)
        );
      case MEDIA_PAUSE:
        return fromPlayerEvent(this._player, EventType.PAUSE).pipe(
          filter(() => this._isSeeking === false),
          tap(() => {
            this._paused = true;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_PLAY:
        return fromPlayerEvent(this._player, EventType.PLAY).pipe(
          filter(() => this._paused || this._isSeeking === false),
          tap(() => {
            this._paused = false;
            this._isBuffering = false;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_PLAYING:
        return fromPlayerEvent(this._player, EventType.MEDIA_STATUS).pipe(
          filter(({ mediaStatus }: CastMediaStatusEvent) => mediaStatus?.playerState === 'PLAYING'),
          filter(() => this._isSeeking === false),
          tap(() => {
            this._paused = false;
            this._isBuffering = false;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_PROGRESS:
        return fromPlayerEvent(this._player, EventType.PROGRESS).pipe(
          filter(() => !this._adsPlaying)
        );
      case MEDIA_RATECHANGE:
        return fromPlayerEvent(this._player, EventType.RATE_CHANGE).pipe(
          filter(() => !this._adsPlaying)
        );
      case MEDIA_SEEKED:
        return fromPlayerEvent(this._player, EventType.SEEKED).pipe(
          tap(() => {
            this._isSeeking = false;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_SEEKING:
        return fromPlayerEvent(this._player, EventType.SEEKING).pipe(
          tap(() => {
            this._isSeeking = true;
          }),
          filter(() => !this._adsPlaying)
        );
      case MEDIA_TIMEUPDATE:
        return fromPlayerEvent(this._player, EventType.TIME_UPDATE).pipe(
          filter(() => !this._adsPlaying),
          map(timeUpdate => ({
            ...timeUpdate,
            currentTime: this.currentTime,
            liveEdge: this.liveEdge,
          }))
        );
      case MEDIA_WAITING:
        return fromPlayerEvent(this._player, EventType.MEDIA_STATUS).pipe(
          filter(
            ({ mediaStatus }: CastMediaStatusEvent) => mediaStatus?.playerState === 'BUFFERING'
          ),
          tap(() => {
            this._isBuffering = true;
            this._readyState = 2;
          }),
          filter(() => !this._adsPlaying)
        );
      case ABR_ADAPTATION:
        return fromPlayerEvent(this._player, EventType.BITRATE_CHANGED).pipe(
          map((abr: CastBitrateChangedEvent) => ({
            bandwidth: abr.totalBitrate,
          }))
        );
      case AD_BREAKBEGIN:
        return fromPlayerEvent(this._player, EventType.BREAK_STARTED).pipe(
          tap(() => {
            this._adsPlaying = true;
          })
        );
      case AD_BREAKEND:
        return fromPlayerEvent(this._player, EventType.BREAK_ENDED).pipe(
          tap(() => {
            this._adsPlaying = false;
          })
        );
      case AD_BEGIN:
        return fromPlayerEvent(this._player, EventType.BREAK_CLIP_STARTED).pipe(
          map((adEvent: framework.events.BreaksEvent) => {
            const breakClip = this._breakManager.getBreakClipById(adEvent.breakClipId ?? '0');
            const { duration, title, id } = breakClip;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const { creativeId } = this._breakManager.getCreativeInfoByClipId(
              adEvent.breakClipId ?? '0'
            );
            return {
              sequence: adEvent.index,
              duration,
              title,
              id,
              creativeId,
            } as mapEvents.AdBeginEvent;
          }),
          map(mapEvents.adBegin)
        );
      case AD_END:
        return fromPlayerEvent(this._player, EventType.BREAK_CLIP_ENDED);
      case AD_TIMEUPDATE:
        return fromPlayerEvent(this._player, EventType.TIME_UPDATE).pipe(
          filter(() => this._adsPlaying)
        );
      case VIEW_SOURCECHANGE:
        return this._content$.asObservable().pipe(
          filter(content => content.src),
          switchMap(() => fromPlayerEvent(this._player, EventType.LOADED_DATA))
        );

      case SCTE_TIMEDEVENTS:
        return fromPlayerEvent(this._player, EventType.TIMED_METADATA_ENTER);
      case PLAYER_LOADING:
        return fromPlayerEvent(this._player, EventType.PLAYER_LOADING);
      case PLAYER_SHUTDOWN:
        return merge(
          fromEvent(this._context as any, this._cast.framework.system.EventType.SHUTDOWN),
          fromPlayerEvent(this._player, EventType.REQUEST_LOAD)
        );
      default:
        return EMPTY;
    }
  }

  get stats() {
    const { height, width, ...otherStats } = this._player.getStats();

    const metrics = {
      corruptedVideoFrames: 0,
      droppedVideoFrames: otherStats.droppedFrames ?? 0,
      currentBandwidthEstimate: otherStats.estimatedBandwidth ?? 0,
      totalBytesLoaded: 0,
      bufferedSegments: {
        amountOfBufferedAudioSegments: 0,
        amountOfBufferedVideoSegments: 0,
      },
    };

    return {
      src: undefined,
      metrics,
      videoHeight: height ?? 0,
      videoWidth: width ?? 0,
      videoTracks: this.videoTracks(),
      audioTracks: this.audioTracks(),
      textTracks: this.textTracks(),
    };
  }

  get liveEdge(): number {
    const liveOrStartOverStreamEnd = (this._player.getLiveSeekableRange() as any)?.end;
    const secondsFromStart = liveOrStartOverStreamEnd ?? this._player.getDurationSec();

    if (this._isLive) {
      return this._player.getAbsoluteTimeForMediaTime(secondsFromStart) ?? 0;
    }

    return secondsFromStart;
  }
}
export default CastAdapter;
